import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Input, Button, message, Row, Col, Typography, Divider, Space 
} from 'antd';
import { 
  UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined, 
  ShopOutlined, GlobalOutlined, SaveOutlined 
} from '@ant-design/icons';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import countryList from 'react-select-country-list';
import { userAPI } from '../api';

const { Title, Text } = Typography;

const MyProfile = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [phoneValue, setPhoneValue] = useState();
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    // 初始化国家列表
    const options = countryList().getData();
    setCountries(options);
    
    // 获取用户资料
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getProfile();
      const data = response.data;
      setProfile(data);
      setPhoneValue(data.phone);
      
      // 设置表单初始值
      form.setFieldsValue({
        username: data.username,
        email: data.email || '',
        name: data.name || '',
        company_name: data.company_name || '',
        country: data.country || '',
        address: data.address || '',
      });
    } catch (error) {
      message.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // 构建更新数据
      const updateData = {
        ...values,
        phone: phoneValue || null,
      };
      
      // 如果密码为空则删除该字段
      if (!updateData.password) {
        delete updateData.password;
      }
      
      await userAPI.updateProfile(updateData);
      message.success('Profile updated successfully');
      
      // 重新获取资料
      fetchProfile();
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(error.response.data.detail);
      } else {
        message.error('Update failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const getCountryName = (countryCode) => {
    if (!countryCode) return '';
    const country = countries.find(c => c.value === countryCode);
    return country ? country.label : countryCode;
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Card>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
          <UserOutlined style={{ marginRight: 8 }} />
          My Profile
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size="large"
        >
          <Divider orientation="left">Basic Information</Divider>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Username"
                name="username"
                rules={[
                  { required: true, message: 'Please enter username' },
                  { min: 3, message: 'Username must be at least 3 characters' },
                  { max: 50, message: 'Username cannot exceed 50 characters' },
                ]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Enter username" 
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="Enter email (optional)" 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Full Name"
                name="name"
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Enter full name (optional)" 
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Phone Number"
              >
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="US"
                  value={phoneValue}
                  onChange={setPhoneValue}
                  style={{
                    padding: '4px 11px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '14px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Company Information</Divider>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Company Name"
                name="company_name"
              >
                <Input 
                  prefix={<ShopOutlined />} 
                  placeholder="Enter company name (optional)" 
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Country"
                name="country"
              >
                <select
                  style={{
                    width: '100%',
                    height: '40px',
                    padding: '4px 11px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select country (optional)</option>
                  {countries.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Address"
            name="address"
          >
            <Input.TextArea 
              prefix={<HomeOutlined />} 
              placeholder="Enter address (optional)"
              rows={3}
            />
          </Form.Item>

          <Divider orientation="left">Security Settings</Divider>
          
          <Form.Item
            label="New Password"
            name="password"
            extra="Leave blank to keep current password"
            rules={[
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password 
              placeholder="Enter new password (optional)" 
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'center', marginTop: 32 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              icon={<SaveOutlined />}
              style={{ minWidth: 120 }}
            >
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default MyProfile;
